import React, { useState, useEffect, useCallback, useMemo } from "react";

import {
  Box,
  Checkbox,
  FormControlLabel,
  Button,
  Typography,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  useMediaQuery,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Swal from "sweetalert2";
import { ColorModeContext, tokens } from "../../../theme";
import Header from "../../../components/Header";
import {
  ExecuteBulkMutationForRights,
  GetRoleRightsByRoleId,
} from "../../../data/Axios/DynamicService";
import { roleManagemenUrl } from "../../../config";
import { debounce } from "lodash";

const PERMISSIONS = [
  "CreatePermission",
  "EditPermission",
  "ActivatePermission",
  "ExportPermission",
];

const AssignRightsFormRevamped = ({ roleRightsData, roleId, onClose }) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken.Id);
  const companyId = parseInt(decodedToken.CompanyId);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [selectedModules, setSelectedModules] = useState({});
  const [selectedRights, setSelectedRights] = useState({});
  const [selectedPermissions, setSelectedPermissions] = useState({});
  const [selectedSubrights, setSelectedSubrights] = useState({});
  const [selectedSubrightPermissions, setSelectedSubrightPermissions] =
    useState({});
  const [loading, setLoading] = useState(true);
  const [rolesbyroleiddata, setRolesByRoleIdData] = useState([]);
  const [expandedModules, setExpandedModules] = useState({});
  const [expandedRights, setExpandedRights] = useState({});
  const [expandedSubrights, setExpandedSubrights] = useState({});

  // Fetch role rights data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const roleRightsDataByRole = await GetRoleRightsByRoleId(roleId);
      if (roleRightsDataByRole?.length > 0) {
        const cleanedData = JSON.parse(roleRightsDataByRole.replace(/\\/g, ""));
        setRolesByRoleIdData(cleanedData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [roleId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Initialize selections based on fetched data
  useEffect(() => {
    if (rolesbyroleiddata.length > 0) {
      initializeSelections();
    }
  }, [rolesbyroleiddata]);

  const initializeSelections = () => {
    const initialSelectedModules = {};
    const initialSelectedRights = {};
    const initialSelectedPermissions = {};
    const initialSelectedSubrights = {};
    const initialSelectedSubrightPermissions = {};

    roleRightsData[0].forEach((module) => {
      initialSelectedModules[module.id] = false;
      initialSelectedRights[module.id] = {};
      initialSelectedPermissions[module.id] = {};
      initialSelectedSubrights[module.id] = {};
      initialSelectedSubrightPermissions[module.id] = {};

      const moduleExists = rolesbyroleiddata.some(
        (item) => item.id === module.id
      );
      if (moduleExists) {
        initialSelectedModules[module.id] = true;

        module.children?.forEach((right) => {
          const moduleData = rolesbyroleiddata.find(
            (item) => item.id === module.id
          );
          if (moduleData?.children) {
            const rightExists = moduleData.children.some(
              (item) => item.id === right.id
            );
            if (rightExists) {
              initialSelectedRights[module.id][right.id] = true;
              initialSelectedPermissions[module.id][right.id] = {};
              initialSelectedSubrights[module.id][right.id] = {};
              initialSelectedSubrightPermissions[module.id][right.id] = {};

              const rightData = moduleData.children.find(
                (item) => item.id === right.id
              );
              if (rightData) {
                PERMISSIONS.forEach((permission) => {
                  initialSelectedPermissions[module.id][right.id][permission] =
                    rightData.permissions[permission] === 1;
                });

                right.subrights?.forEach((subright) => {
                  const subrightExists = rightData.subrights?.some(
                    (item) => item.id === subright.id
                  );
                  if (subrightExists) {
                    initialSelectedSubrights[module.id][right.id][
                      subright.id
                    ] = true;
                    initialSelectedSubrightPermissions[module.id][right.id][
                      subright.id
                    ] = {};

                    const subrightData = rightData.subrights.find(
                      (item) => item.id === subright.id
                    );
                    if (subrightData) {
                      PERMISSIONS.forEach((permission) => {
                        initialSelectedSubrightPermissions[module.id][right.id][
                          subright.id
                        ][permission] =
                          subrightData.permissions[permission] === 1;
                      });
                    }
                  } else {
                    initialSelectedSubrights[module.id][right.id][
                      subright.id
                    ] = false;
                    initialSelectedSubrightPermissions[module.id][right.id][
                      subright.id
                    ] = {};
                  }
                });
              }
            }
          }
        });
      }
    });

    setSelectedModules(initialSelectedModules);
    setSelectedRights(initialSelectedRights);
    setSelectedPermissions(initialSelectedPermissions);
    setSelectedSubrights(initialSelectedSubrights);
    setSelectedSubrightPermissions(initialSelectedSubrightPermissions);
  };

  // Debounced handleCheckboxChange
  const debouncedHandleCheckboxChange = useCallback(
    debounce((moduleId, rightId, permission, isChecked, subrightId) => {
      if (moduleId && !rightId && !permission && !subrightId) {
        // Module checkbox clicked
        const updatedRights = {};
        const updatedPermissions = {};
        const updatedSubrights = {};
        const updatedSubrightPermissions = {};

        const module = roleRightsData[0].find((m) => m.id === moduleId);
        if (module) {
          module.children?.forEach((right) => {
            updatedRights[right.id] = isChecked;

            // Ensure PERMISSIONS is an array before using Object.fromEntries
            updatedPermissions[right.id] = Object.fromEntries(
              (PERMISSIONS || []).map((perm) => [perm, isChecked])
            );

            updatedSubrights[right.id] = {};
            updatedSubrightPermissions[right.id] = {};

            right.subrights?.forEach((subright) => {
              updatedSubrights[right.id][subright.id] = isChecked;
              updatedSubrightPermissions[right.id][subright.id] =
                Object.fromEntries(
                  (PERMISSIONS || []).map((perm) => [perm, isChecked])
                );
            });
          });

          if (isChecked) {
            setExpandedModules((prev) => ({ ...prev, [moduleId]: true }));
            setExpandedRights((prev) => ({
              ...prev,
              [moduleId]: Object.fromEntries(
                module.children?.map((right) => [right.id, true]) || []
              ),
            }));
            setExpandedSubrights((prev) => ({
              ...prev,
              [moduleId]: Object.fromEntries(
                module.children?.map((right) => [
                  right.id,
                  Object.fromEntries(
                    right.subrights?.map((subright) => [subright.id, true]) || []
                  ),
                ]) || []
              ),
            }));
          } else {
            setExpandedModules((prev) => ({ ...prev, [moduleId]: false }));
            setExpandedRights((prev) => ({ ...prev, [moduleId]: {} }));
            setExpandedSubrights((prev) => ({ ...prev, [moduleId]: {} }));
          }
        }

        setSelectedModules((prev) => ({ ...prev, [moduleId]: isChecked }));
        setSelectedRights((prev) => ({ ...prev, [moduleId]: updatedRights }));
        setSelectedPermissions((prev) => ({
          ...prev,
          [moduleId]: updatedPermissions,
        }));
        setSelectedSubrights((prev) => ({
          ...prev,
          [moduleId]: updatedSubrights,
        }));
        setSelectedSubrightPermissions((prev) => ({
          ...prev,
          [moduleId]: updatedSubrightPermissions,
        }));
      } else if (moduleId && rightId && !permission && !subrightId) {
        // Right checkbox clicked
        const updatedPermissions = Object.fromEntries(
          (PERMISSIONS || []).map((perm) => [perm, isChecked])
        );
        const updatedSubrights = {};
        const updatedSubrightPermissions = {};

        const right = roleRightsData[0]
          .find((m) => m.id === moduleId)
          ?.children?.find((r) => r.id === rightId);
        if (right) {
          right.subrights?.forEach((subright) => {
            updatedSubrights[subright.id] = isChecked;
            updatedSubrightPermissions[subright.id] = Object.fromEntries(
              (PERMISSIONS || []).map((perm) => [perm, isChecked])
            );
          });

          if (isChecked) {
            setExpandedRights((prev) => ({
              ...prev,
              [moduleId]: { ...prev[moduleId], [rightId]: true },
            }));
            setExpandedSubrights((prev) => ({
              ...prev,
              [moduleId]: {
                ...prev[moduleId],
                [rightId]: Object.fromEntries(
                  right.subrights?.map((subright) => [subright.id, true]) || []
                ),
              },
            }));
          }
        }

        setSelectedRights((prev) => ({
          ...prev,
          [moduleId]: { ...prev[moduleId], [rightId]: isChecked },
        }));
        setSelectedPermissions((prev) => ({
          ...prev,
          [moduleId]: { ...prev[moduleId], [rightId]: updatedPermissions },
        }));
        setSelectedSubrights((prev) => ({
          ...prev,
          [moduleId]: { ...prev[moduleId], [rightId]: updatedSubrights },
        }));
        setSelectedSubrightPermissions((prev) => ({
          ...prev,
          [moduleId]: {
            ...prev[moduleId],
            [rightId]: updatedSubrightPermissions,
          },
        }));

        // Automatically check the parent module if a right is checked
        if (isChecked) {
          setSelectedModules((prev) => ({
            ...prev,
            [moduleId]: true,
          }));
        }
      } else if (moduleId && rightId && permission && !subrightId) {
        // Permission checkbox clicked
        setSelectedPermissions((prev) => ({
          ...prev,
          [moduleId]: {
            ...prev[moduleId],
            [rightId]: {
              ...prev[moduleId][rightId],
              [permission]: isChecked,
            },
          },
        }));

        // Automatically check the parent right if a permission is checked
        if (isChecked) {
          setSelectedRights((prev) => ({
            ...prev,
            [moduleId]: {
              ...prev[moduleId],
              [rightId]: true,
            },
          }));
          setSelectedModules((prev) => ({
            ...prev,
            [moduleId]: true,
          }));
        }
      } else if (moduleId && rightId && subrightId && !permission) {
        // Sub-right checkbox clicked
        const updatedPermissions = Object.fromEntries(
          (PERMISSIONS || []).map((perm) => [perm, isChecked])
        );
        setSelectedSubrights((prev) => ({
          ...prev,
          [moduleId]: {
            ...prev[moduleId],
            [rightId]: {
              ...prev[moduleId][rightId],
              [subrightId]: isChecked,
            },
          },
        }));
        setSelectedSubrightPermissions((prev) => ({
          ...prev,
          [moduleId]: {
            ...prev[moduleId],
            [rightId]: {
              ...prev[moduleId][rightId],
              [subrightId]: updatedPermissions,
            },
          },
        }));

        // Automatically check the parent right if a sub-right is checked
        if (isChecked) {
          setSelectedRights((prev) => ({
            ...prev,
            [moduleId]: {
              ...prev[moduleId],
              [rightId]: true,
            },
          }));
          setSelectedModules((prev) => ({
            ...prev,
            [moduleId]: true,
          }));
        }
      } else if (moduleId && rightId && subrightId && permission) {
        // Sub-right permission checkbox clicked
        setSelectedSubrightPermissions((prev) => ({
          ...prev,
          [moduleId]: {
            ...prev[moduleId],
            [rightId]: {
              ...prev[moduleId][rightId],
              [subrightId]: {
                ...prev[moduleId][rightId][subrightId],
                [permission]: isChecked,
              },
            },
          },
        }));

        // Automatically check the parent sub-right and right if a permission is checked
        if (isChecked) {
          setSelectedSubrights((prev) => ({
            ...prev,
            [moduleId]: {
              ...prev[moduleId],
              [rightId]: {
                ...prev[moduleId][rightId],
                [subrightId]: true,
              },
            },
          }));
          setSelectedRights((prev) => ({
            ...prev,
            [moduleId]: {
              ...prev[moduleId],
              [rightId]: true,
            },
          }));
          setSelectedModules((prev) => ({
            ...prev,
            [moduleId]: true,
          }));
        }
      }
    }, 300), // Debounce for 300ms
    [roleRightsData, PERMISSIONS]
  );

  const handleCheckboxChange = useCallback(
    (moduleId, rightId, permission, isChecked, subrightId) => {
      // Immediate state update without debounce for module-level changes
      if (moduleId && !rightId && !permission && !subrightId) {
        // Module checkbox clicked - cascade to all children
        const module = roleRightsData[0].find((m) => m.id === moduleId);
        if (!module) return;
  
        // Create updated states for all children
        const updatedRights = {};
        const updatedPermissions = {};
        const updatedSubrights = {};
        const updatedSubrightPermissions = {};
  
        // Process all rights under this module
        module.children?.forEach((right) => {
          updatedRights[right.id] = isChecked;
          updatedPermissions[right.id] = {};
          
          // Set all permissions for this right
          PERMISSIONS.forEach(perm => {
            updatedPermissions[right.id][perm] = isChecked;
          });
  
          updatedSubrights[right.id] = {};
          updatedSubrightPermissions[right.id] = {};
  
          // Process all subrights under this right
          right.subrights?.forEach((subright) => {
            updatedSubrights[right.id][subright.id] = isChecked;
            updatedSubrightPermissions[right.id][subright.id] = {};
            
            // Set all permissions for this subright
            PERMISSIONS.forEach(perm => {
              updatedSubrightPermissions[right.id][subright.id][perm] = isChecked;
            });
          });
        });
  
        // Update all states in one go
        setSelectedModules(prev => ({ ...prev, [moduleId]: isChecked }));
        setSelectedRights(prev => ({ ...prev, [moduleId]: updatedRights }));
        setSelectedPermissions(prev => ({ ...prev, [moduleId]: updatedPermissions }));
        setSelectedSubrights(prev => ({ ...prev, [moduleId]: updatedSubrights }));
        setSelectedSubrightPermissions(prev => ({
          ...prev,
          [moduleId]: updatedSubrightPermissions
        }));
  
        // Expand all when checked, collapse when unchecked
        if (isChecked) {
          const rightsExpanded = {};
          const subrightsExpanded = {};
          
          module.children?.forEach(right => {
            rightsExpanded[right.id] = true;
            subrightsExpanded[right.id] = {};
            right.subrights?.forEach(subright => {
              subrightsExpanded[right.id][subright.id] = true;
            });
          });
          
          setExpandedModules(prev => ({ ...prev, [moduleId]: true }));
          setExpandedRights(prev => ({ ...prev, [moduleId]: rightsExpanded }));
          setExpandedSubrights(prev => ({ ...prev, [moduleId]: subrightsExpanded }));
        } else {
          setExpandedModules(prev => ({ ...prev, [moduleId]: false }));
        }
      } 
      // ... rest of your existing conditions for right, permission, and subright checkboxes
      else {
        // Use debounced version for other changes
        debouncedHandleCheckboxChange(
          moduleId,
          rightId,
          permission,
          isChecked,
          subrightId
        );
      }
    },
    [roleRightsData, PERMISSIONS, debouncedHandleCheckboxChange]
  );
  const flattenPermissions = (permissions) => {
    return Object.keys(permissions).flatMap((moduleId) =>
      Object.keys(permissions[moduleId] || {}).flatMap((rightId) => {
        const currentPermissions = permissions[moduleId][rightId];
        return {
          moduleId: parseInt(moduleId),
          rightId: parseInt(rightId),
          roleId, // Ensure roleId is defined in the scope
          createdBy: userId, // Ensure userId is defined in the scope
          companyId: companyId,
          createPermission: currentPermissions.includes("CreatePermission")
            ? 1
            : 0,
          editPermission: currentPermissions.includes("EditPermission") ? 1 : 0,
          activatePermission: currentPermissions.includes("ActivatePermission")
            ? 1
            : 0,
          exportPermission: currentPermissions.includes("ExportPermission")
            ? 1
            : 0,
          statusFlag: null,
          createdDate: new Date().toISOString(),
          updatedBy: userId,
          updatedDate: new Date().toISOString(),
          subRights: [],
        };
      })
    );
  };
  const flattenSubrightPermissions = (permissions) => {
    return Object.keys(permissions).flatMap((moduleId) =>
      Object.keys(permissions[moduleId] || {}).flatMap((rightId) =>
        Object.keys(permissions[moduleId][rightId] || {}).flatMap(
          (subrightId) => {
            const currentPermissions =
              permissions[moduleId][rightId][subrightId];
            return {
              roleId: parseInt(roleId),
              rightId: parseInt(rightId),
              subRightId: parseInt(subrightId),
              subRight: currentPermissions.subRightName || "",
              createPermission: currentPermissions.includes("CreatePermission")
                ? 1
                : 0,
              editPermission: currentPermissions.includes("EditPermission")
                ? 1
                : 0,
              activatePermission: currentPermissions.includes(
                "ActivatePermission"
              )
                ? 1
                : 0,
              exportPermission: currentPermissions.includes("ExportPermission")
                ? 1
                : 0,
              statusFlag: null,
              createdBy: userId,
              createdDate: new Date().toISOString(),
              updatedBy: userId,
              updatedDate: new Date().toISOString(),
              companyId: companyId,
            };
          }
        )
      )
    );
  };
  const combinePermissions = (mainPermissions, subrightPermissions) => {
    const permissionMap = {};

    mainPermissions.forEach((permission) => {
      const key = `${permission.moduleId}-${permission.rightId}`;
      permissionMap[key] = permission;
    });

    subrightPermissions.forEach((subright) => {
      const key = `${subright.rightId}`;
      if (permissionMap[key]) {
        permissionMap[key].subRights.push(subright);
      } else {
        permissionMap[key] = {
          moduleId: parseInt(subright.moduleId),
          rightId: parseInt(subright.rightId),
          roleId: parseInt(subright.roleId),
          createPermission: 0,
          editPermission: 0,
          activatePermission: 0,
          exportPermission: 0,
          statusFlag: null,
          createdDate: new Date().toISOString(),
          updatedBy: userId,
          updatedDate: new Date().toISOString(),
          subRights: [subright],
          companyId: companyId,
        };
      }
    });

    return Object.values(permissionMap);
  };
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const mainPermissions = flattenPermissions(consolidatedPermissions);
      const subrightPermissions = flattenSubrightPermissions(
        consolidatedSubrightPermissions
      );
      const combinedPermissions = combinePermissions(
        mainPermissions,
        subrightPermissions
      );

      const response = await ExecuteBulkMutationForRights(
        "createRoleRightsAndSubRightsBulk",
        "newRoleRights",
        combinedPermissions,
        roleManagemenUrl.uri
      );

      if (Object.values(response)[0] != null) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Permissions updated successfully!",
        });
        onClose();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An error occurred while updating permissions.",
      });
      console.error("Error updating permissions:", error);
    } finally {
      setLoading(false);
    }
  };

  const consolidatedPermissions = useMemo(() => {
    const permissions = {};
    Object.keys(selectedModules).forEach((moduleId) => {
      if (selectedModules[moduleId]) {
        permissions[moduleId] = {};
        Object.keys(selectedRights[moduleId] || {}).forEach((rightId) => {
          if (selectedRights[moduleId][rightId]) {
            permissions[moduleId][rightId] = Object.keys(
              selectedPermissions[moduleId][rightId] || {}
            ).filter(
              (permission) => selectedPermissions[moduleId][rightId][permission]
            );
          }
        });
      }
    });
    return permissions;
  }, [selectedModules, selectedRights, selectedPermissions]);

  const consolidatedSubrightPermissions = useMemo(() => {
    const permissions = {};
    Object.keys(selectedModules).forEach((moduleId) => {
      if (selectedModules[moduleId]) {
        permissions[moduleId] = {};
        Object.keys(selectedRights[moduleId] || {}).forEach((rightId) => {
          if (selectedRights[moduleId][rightId]) {
            permissions[moduleId][rightId] = {};
            Object.keys(selectedSubrights[moduleId][rightId] || {}).forEach(
              (subrightId) => {
                if (selectedSubrights[moduleId][rightId][subrightId]) {
                  permissions[moduleId][rightId][subrightId] = Object.keys(
                    selectedSubrightPermissions[moduleId][rightId][
                      subrightId
                    ] || {}
                  ).filter(
                    (permission) =>
                      selectedSubrightPermissions[moduleId][rightId][
                        subrightId
                      ][permission]
                  );
                }
              }
            );
          }
        });
      }
    });
    return permissions;
  }, [
    selectedModules,
    selectedRights,
    selectedSubrights,
    selectedSubrightPermissions,
  ]);

  return (
    <Box m={isMobile ? "10px" : "20px"}>
      <Header
        title="Assign Rights"
        subtitle="Assign rights to the selected role"
      />
      <Box sx={{ overflowX: "auto" }}>
        {roleRightsData[0]?.map((module) => (
          <ModuleAccordion
            key={module.id}
            module={module}
            selectedModules={selectedModules}
            selectedRights={selectedRights}
            selectedPermissions={selectedPermissions}
            selectedSubrights={selectedSubrights}
            selectedSubrightPermissions={selectedSubrightPermissions}
            expandedModules={expandedModules}
            expandedRights={expandedRights}
            expandedSubrights={expandedSubrights}
            handleCheckboxChange={handleCheckboxChange}
            setExpandedModules={setExpandedModules}
            setExpandedRights={setExpandedRights}
            setExpandedSubrights={setExpandedSubrights}
            colors={colors}
            isMobile={isMobile}
          />
        ))}
      </Box>
      <Box mt={4} display="flex" flexDirection={isMobile ? "column" : "row"} gap={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading}
          fullWidth={isMobile}
          sx={{ minWidth: isMobile ? "100%" : "auto" }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Save"}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={onClose}
          fullWidth={isMobile}
          sx={{minWidth: isMobile ? "100%" : "auto",
                            color: colors.greenAccent[400], // unchecked color
                            '&.Mui-checked': {
                            color: colors.greenAccent[400], // checked color
                            },
            
                        }}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

const ModuleAccordion = React.memo(
  ({
    module,
    selectedModules,
    selectedRights,
    selectedPermissions,
    selectedSubrights,
    selectedSubrightPermissions,
    expandedModules,
    expandedRights,
    expandedSubrights,
    handleCheckboxChange,
    setExpandedModules,
    setExpandedRights,
    setExpandedSubrights,
    colors,
    isMobile,
  }) => {
    return (
      <Accordion
        expanded={expandedModules[module.id] || false}
        onChange={() =>
          setExpandedModules((prev) => ({
            ...prev,
            [module.id]: !prev[module.id],
          }))
        }
          sx={{
            width: isMobile ? '100%' : '100%',
            paddingLeft: isMobile ? 50 : 0,
            maxWidth: '100%',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            overflow: 'hidden',
            transition: 'width 0.3s ease',
          }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{ padding: isMobile ? "8px" : "16px" }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={!!selectedModules[module.id]}
                onChange={(e) =>
                  handleCheckboxChange(module.id, null, null, e.target.checked)
                }
                size={isMobile ? "small" : "medium"}
                sx={{
                    padding: isMobile ? "4px" : "8px",
                            color: colors.greenAccent[400], // unchecked color
                            '&.Mui-checked': {
                            color: colors.greenAccent[400], // checked color
                            },
                        }}
              />
            }
            label={
              <Typography 
                color={colors.greenAccent[400]} 
                variant={isMobile ? "body2" : "body1"}
              >
                {module.title}
              </Typography>
            }
            sx={{ width: "100%", mr: 0 }}
          />
        </AccordionSummary>
        <AccordionDetails sx={{ p: isMobile ? "8px" : "16px" }}>
          {expandedModules[module.id] &&
            module.children?.map((right) => (
              <RightAccordion
                key={right.id}
                moduleId={module.id}
                right={right}
                selectedRights={selectedRights}
                selectedPermissions={selectedPermissions}
                selectedSubrights={selectedSubrights}
                selectedSubrightPermissions={selectedSubrightPermissions}
                expandedRights={expandedRights}
                expandedSubrights={expandedSubrights}
                handleCheckboxChange={handleCheckboxChange}
                setExpandedRights={setExpandedRights}
                setExpandedSubrights={setExpandedSubrights}
                colors={colors}
                isMobile={isMobile}
              />
            ))}
        </AccordionDetails>
      </Accordion>
    );
  }
);

const RightAccordion = React.memo(
  ({
    moduleId,
    right,
    selectedRights,
    selectedPermissions,
    selectedSubrights,
    selectedSubrightPermissions,
    expandedRights,
    expandedSubrights,
    handleCheckboxChange,
    setExpandedRights,
    setExpandedSubrights,
    colors,
    isMobile,
  }) => {
    return (
      <Accordion
        expanded={expandedRights[moduleId]?.[right.id] || false}
        onChange={() =>
          setExpandedRights((prev) => ({
            ...prev,
            [moduleId]: {
              ...prev[moduleId],
              [right.id]: !prev[moduleId]?.[right.id],
            },
          }))
        }
        sx={{ mb: 1, ml: isMobile ? 0 : 2 }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{ padding: isMobile ? "6px" : "12px" }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={!!selectedRights[moduleId]?.[right.id] || false}
                onChange={(e) =>
                  handleCheckboxChange(
                    moduleId,
                    right.id,
                    null,
                    e.target.checked
                  )
                }
                size={isMobile ? "small" : "medium"}
               sx={{
                            color: colors.greenAccent[400], // unchecked color
                            '&.Mui-checked': {
                            color: colors.greenAccent[400], // checked color
                            },
                        }}
              />
            }
            label={
              <Typography 
                color={colors.blueAccent[400]} 
                variant={isMobile ? "body2" : "body1"}
              >
                {right.title}
              </Typography>
            }
            sx={{ width: "100%", mr: 0 }}
          />
        </AccordionSummary>
        <AccordionDetails sx={{ p: isMobile ? "6px" : "12px" }}>
          {expandedRights[moduleId]?.[right.id] && (
            <>
              <Grid container spacing={1}>
                {PERMISSIONS.map((permission) => (
                  <Grid item xs={isMobile ? 6 : 3} key={permission}>
                    <FormControlLabel
                      control={
                        <Checkbox
                        key={permission}
                        checked={!!selectedPermissions[moduleId]?.[right.id]?.[permission]}
                        onChange={(e) => handleCheckboxChange(moduleId, right.id, permission, e.target.checked)}
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            color: colors.greenAccent[400], // unchecked color
                            '&.Mui-checked': {
                            color: colors.greenAccent[400], // checked color
                            },
                        }}
                        />
                      }
                      label={
                        <Typography variant={isMobile ? "caption" : "body2"}>
                          {permission}
                        </Typography>
                      }
                    />
                  </Grid>
                ))}
              </Grid>
              {right.subrights?.map((subright) => (
                <SubrightAccordion
                  key={subright.id}
                  moduleId={moduleId}
                  rightId={right.id}
                  subright={subright}
                  selectedSubrights={selectedSubrights}
                  selectedSubrightPermissions={selectedSubrightPermissions}
                  expandedSubrights={expandedSubrights}
                  handleCheckboxChange={handleCheckboxChange}
                  setExpandedSubrights={setExpandedSubrights}
                  colors={colors}
                  isMobile={isMobile}
                />
              ))}
            </>
          )}
        </AccordionDetails>
      </Accordion>
    );
  }
);

const SubrightAccordion = React.memo(({
  moduleId,
  rightId,
  subright,
  selectedSubrights,
  selectedSubrightPermissions,
  expandedSubrights,
  handleCheckboxChange,
  setExpandedSubrights,
  isMobile,
  colors
}) => {
  return (
    <Accordion
      expanded={expandedSubrights[moduleId]?.[rightId]?.[subright.id] || false}
      onChange={() =>
        setExpandedSubrights((prev) => ({
          ...prev,
          [moduleId]: {
            ...prev[moduleId],
            [rightId]: {
              ...prev[moduleId]?.[rightId],
              [subright.id]: !prev[moduleId]?.[rightId]?.[subright.id],
            },
          },
        }))
      }
      sx={{ mb: 1, ml: isMobile ? 0 : 4 }}
    >
      <AccordionSummary 
        expandIcon={<ExpandMoreIcon />}
        sx={{ padding: isMobile ? "4px" : "8px" }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={
                selectedSubrights[moduleId]?.[rightId]?.[subright.id] || false
              }
              onChange={(e) =>
                handleCheckboxChange(
                  moduleId,
                  rightId,
                  null,
                  e.target.checked,
                  subright.id
                )
              }
              size={isMobile ? "small" : "medium"}
              sx={{
                            color: colors.greenAccent[400], // unchecked color
                            '&.Mui-checked': {
                            color: colors.greenAccent[400], // checked color
                            },
                        }}
            />
          }
          label={
            <Typography variant={isMobile ? "caption" : "body2"}>
              {subright.title}
            </Typography>
          }
          sx={{ width: "100%", mr: 0 }}
        />
      </AccordionSummary>
      <AccordionDetails sx={{ p: isMobile ? "4px" : "8px" }}>
        {expandedSubrights[moduleId]?.[rightId]?.[subright.id] && (
          <Grid container spacing={1}>
            {PERMISSIONS.map((permission) => (
              <Grid item xs={isMobile ? 6 : 3} key={permission}>
                <FormControlLabel
                  control={
                    <Checkbox 
                        checked={
                            selectedSubrightPermissions[moduleId]?.[rightId]?.[subright.id]?.[permission] || false
                        }
                        onChange={(e) =>
                            handleCheckboxChange(
                            moduleId,
                            rightId,
                            permission,
                            e.target.checked,
                            subright.id
                            )
                        }
                        size={isMobile ? "small" : "medium"}
                        sx={{
                            color: colors.greenAccent[400], // unchecked color
                            '&.Mui-checked': {
                            color: colors.greenAccent[400], // checked color
                            },
                        }}
                        />
                  }
                  label={
                    <Typography variant={isMobile ? "caption" : "body2"}>
                      {permission}
                    </Typography>
                  }
                />
                </Grid>
              ))}
            </Grid>
          )}
        </AccordionDetails>
      </Accordion>
    );
  }
);

export default AssignRightsFormRevamped;